import React, { useContext } from "react";
import moment from "moment";
import { DocumentType } from "src/utils/enum";
import { formatMoneyWithCurrency, formatRut } from "../../../../../../utils/formatter";
import { formatBanks } from "../../../../../../utils/bankList";
import { Icon, Popup } from "semantic-ui-react";
import { UserContext, CompanyContext } from "src/context";
import { showAccounting } from "src/utils/accounting";
import { useBlockedFeatures } from "src/hooks/useBlockedFeatures";
import { dt } from "src/utils/documentsI18n";
import "./MatchRow.scss";

/**
 * MatchItem - The grey block component showing match details
 * Displays the avatar, document info, date, amount, and description
 */
const MatchItem = ({
  match,
  /** Match del movimiento / inMatch para entries.completed; si no viene, se usa match. */
  matchForAccounting,
  documentLabelWithNumber,
  folioDetalle,
  matchDate,
  amount,
  currency,
  name,
  onDocumentClick,
  onTransactionClick,
  onGoToMovementClick,
  onDeleteClick,
  onContabilizarClick,
  onRevisarMatchClick,
  originalAmount,
  exchangeRate,
  originalCurrency,
  isManualMatch = false,
  isClayMatch = false,
  transactionType,
}) => {
  const { roles } = useContext(UserContext);
  const { selectedCompany } = useContext(CompanyContext);
  const { isFeatureBlocked } = useBlockedFeatures();
  const isAsientoBlocked = isFeatureBlocked('asiento');
  const shouldShowAccounting = showAccounting(roles, selectedCompany);
  const isDocumentClickable = [DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION, DocumentType.CREDIT].includes(match.documentType);
  const isTransactionClickable = ["SPEND", "GIVEN", "RECIEVED", "RECEIVED"].includes(match.type);
  const isTargetClickable = isTransactionClickable || isDocumentClickable;
  const isClickable = isDocumentClickable || isTransactionClickable;
  const accountingSource = matchForAccounting ?? match;
  const isAccountingCompleted = Boolean(
    accountingSource?.entries?.completed ||
    accountingSource?.transaction?.entries?.completed
  );

  const matchType = (match?.type || '').toUpperCase();

  // Color del monto: rojo = cargo/egreso (--received), verde = abono/ingreso (--given).
  // Respetar el tipo de la transacción cuando el match es una obligación para no mostrar signo contrario.
  const isDebit = (() => {
    if (matchType === 'SPEND') return true;
    if ([DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION, DocumentType.CREDIT].includes(match.documentType) && match.meta?.isReceived !== undefined) {
      return !!match.meta.isReceived;
    }
    // Obligation or Credit: si tenemos tipo de transacción (contexto de conciliación), usar su signo para el color
    if ([DocumentType.OBLIGATION, DocumentType.CREDIT].includes(match.documentType) && transactionType) {
      return transactionType === 'SPEND'; // SPEND → rojo (cargo), RECIEVED → verde (abono)
    }
    if ([DocumentType.OBLIGATION, DocumentType.CREDIT].includes(match.documentType)) return true;
    return false;
  })();

  // Generate avatar initials
  const getAvatarInitials = () => {
    if (documentLabelWithNumber) {
      const words = documentLabelWithNumber.split(' ');
      if (words.length >= 2) {
        const firstInitial = words[0][0];
        const secondInitial = words[1][0];
        // Check if second initial is a letter
        if (/[a-zA-Z]/.test(secondInitial)) {
          return (firstInitial + secondInitial).toUpperCase();
        } else {
          // If second initial is not a letter, use the second letter of the first word
          const secondLetterOfFirst = words[0].length > 1 ? words[0][1] : firstInitial;
          return (firstInitial + secondLetterOfFirst).toUpperCase();
        }
      }
      return documentLabelWithNumber.substring(0, 2).toUpperCase();
    }
    return 'DC'; // Default: Documento Conciliado
  };

  return (
    <div className="match-row-item">
      <div className="match-row-avatar">
        {getAvatarInitials()}
      </div>
      <div className="match-row-body">
        <div className="match-row-header">
          <div className={`match-row-user ${isClickable ? 'match-row-user--clickable' : ''}`}>
            {isDocumentClickable ? (
              <span onClick={onDocumentClick}>
                {documentLabelWithNumber}
              </span>
            ) : isTransactionClickable ? (
              <span onClick={onTransactionClick}>
                {documentLabelWithNumber}
              </span>
            ) : (
              documentLabelWithNumber
            )}
            {folioDetalle}
          </div>
          <div className="match-row-time">
            {isTransactionClickable ? dt('reconcile.withDate') : dt('reconcile.issuedOn')}{' '}
            {matchDate.format('DD/MM/YY')}
          </div>
          {isTargetClickable && onGoToMovementClick && (
            <button
              type="button"
              className="match-row-view-movement"
              onClick={onGoToMovementClick}
            >
              <span>{dt('reconcile.view')}</span>
              <Icon name="arrow up right" className="match-row-view-movement-icon" />
            </button>
          )}
          <div className={`match-row-amount-container ${isDebit ? 'match-row-amount-container--received' : 'match-row-amount-container--given'}`}>
            <span className="match-row-amount-text">
              {formatMoneyWithCurrency(Math.abs(amount), currency)}
            </span>
            {shouldShowAccounting && !isAsientoBlocked && (onContabilizarClick || isAccountingCompleted) && (
              <Popup
                trigger={
                  <Icon
                    name={isAccountingCompleted ? "check circle" : "book"}
                    size="small"
                    className={`match-row-contabilizar-icon${isAccountingCompleted ? ' match-row-contabilizar-icon--completed' : ''}`}
                    style={isAccountingCompleted ? { opacity: 0.65, cursor: "not-allowed", color: "var(--color-success, #16a34a)" } : {}}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isAccountingCompleted && onContabilizarClick) {
                        onContabilizarClick();
                      }
                    }}
                  />
                }
                content={
                  isAccountingCompleted
                    ? dt('reconcile.popups.entryCreated')
                    : dt('reconcile.popups.createEntry')
                }
                inverted
                position="top center"
                size="tiny"
              />
            )}
            {onRevisarMatchClick && (
              <Popup
                trigger={
                  <Icon
                    name="search"
                    size="small"
                    className="match-row-revisar-match-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRevisarMatchClick();
                    }}
                  />
                }
                content={dt('reconcile.popups.categorizeMatch')}
                inverted
                position="top center"
                size="tiny"
              />
            )}
            {onDeleteClick && (
              <Popup
                trigger={
                  <Icon
                    name="trash"
                    size="small"
                    className="match-row-delete-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteClick();
                    }}
                  />
                }
                content={dt('reconcile.popups.deleteReconciliation')}
                inverted
                position="top center"
                size="tiny"
              />
            )}
          </div>
        </div>
        <div className="match-row-text">
          {isManualMatch
            ? dt('reconcile.settledManually')
            : isClayMatch
              ? dt('reconcile.settledInClay')
              : name}
        </div>
        {(() => {
          // Datos de garantía (obligaciones Capitaria): se muestran debajo en fuente pequeña/gris.
          const m = match?.meta;
          if (match?.documentType !== 'OBLIGATION' || !m) return null;
          if (!m.login && !m.ticket && !m.transactionNumber) return null;
          const parts = [];
          if (m.receiverId) parts.push(`RUT: ${m.receiverId}`);
          if (m.login) parts.push(`Login: ${m.login}`);
          if (m.transactionNumber) parts.push(`N° Transacción: ${m.transactionNumber}`);
          if (m.ticket) parts.push(`Ticket: ${m.ticket}`);
          if (m.empresa?.name) parts.push(`Empresa: ${m.empresa.name}`);
          return <div className="match-row-subfields">{parts.join(' · ')}</div>;
        })()}
        {(() => {
          const rateNum = Number(exchangeRate);
          const foreignAmt =
            originalAmount != null && originalAmount !== ''
              ? Math.abs(Number(originalAmount))
              : null;
          const showFx =
            Number.isFinite(rateNum) &&
            rateNum > 1 &&
            foreignAmt != null &&
            Number.isFinite(foreignAmt) &&
            foreignAmt > 0;
          if (!showFx) return null;
          const curr = originalCurrency || match.currency || currency || 'CLP';
          return (
            <div
              style={{
                fontSize: '11px',
                fontStyle: 'italic',
                color: '#6B7280',
                marginTop: '4px',
              }}
            >
              {dt('reconcile.paidAtRate', {
                amount: formatMoneyWithCurrency(foreignAmt, curr),
                rate: rateNum.toFixed(2).replace('.', ','),
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default MatchItem;

